<template>
  <section class="p-6 max-w-5xl mx-auto">
    <div class="flex items-end justify-between mb-5 gap-4 flex-wrap">
      <div>
        <h1 class="font-heading font-bold text-xl">Simulate a flow</h1>
        <p class="text-[13px] text-gray-500 dark:text-dark-text-muted mt-1">
          Configure a scenario and preview exactly which messages would send,
          when, and with what content
        </p>
      </div>
      <button
        class="btn btn-primary"
        :disabled="simRunning"
        @click="runSimulation"
      >
        <play-icon class="w-4 h-4" />
        <span>{{ simRunning ? "Running…" : "Run Simulation" }}</span>
      </button>
    </div>

    <div class="grid md:grid-cols-2 gap-5 mb-5">
      <!-- Scenario -->
      <div class="card">
        <div class="card-header"><div class="card-title">⚙️ Scenario</div></div>
        <div class="card-body space-y-4">
          <div>
            <label class="field-label">Delivery type</label>
            <select class="field" v-model="simType">
              <option value="driver">Callia Driver</option>
              <option value="courier">Courier</option>
            </select>
          </div>
          <div>
            <label class="field-label">Building type</label>
            <select class="field" v-model="simBuilding">
              <option value="house">House</option>
              <option value="townhouse">Townhouse</option>
              <option value="apartment">Apartment</option>
              <option value="business">Business</option>
              <option value="hospital">Hospital</option>
            </select>
          </div>
          <div>
            <label class="field-label">Delivery window</label>
            <select class="field" v-model="simWindow">
              <option value="morning">Morning (8:00 AM – 12:00 PM)</option>
              <option value="afternoon">Afternoon (12:00 PM – 5:00 PM)</option>
              <option value="evening">Evening (5:00 PM – 8:00 PM)</option>
            </select>
          </div>
          <div>
            <label class="field-label">Weather on delivery day</label>
            <select class="field" v-model="simWeather">
              <option value="perfect">Perfect 🌤</option>
              <option value="hot">Hot ☀️</option>
              <option value="cold">Cold 🌨</option>
            </select>
          </div>
        </div>
      </div>

      <!-- Order parameters -->
      <div class="card">
        <div class="card-header">
          <div class="card-title">🧪 Order parameters</div>
        </div>
        <div class="card-body space-y-4">
          <div>
            <label class="field-label"
              >Recipient consented to day-before text?</label
            >
            <select class="field" v-model="simConsent">
              <option value="yes">Yes — opted in at checkout</option>
              <option value="no">No — did not opt in</option>
            </select>
          </div>
          <div>
            <label class="field-label"
              >Order placed relative to day-before trigger cutoff</label
            >
            <select class="field" v-model="simTiming">
              <option value="before"
                >Before 10:00 AM day-before (Batch 1 eligible)</option
              >
              <option value="after-b1"
                >After 10:00 AM day-before, before 8:00 AM day-of (Batch
                2)</option
              >
              <option value="after-b2"
                >After 8:00 AM day-of — at order pull (Batch 3)</option
              >
            </select>
          </div>
          <div>
            <label class="field-label">Flow version to preview</label>
            <select class="field" v-model="simVersion">
              <option value="live">Live (current)</option>
              <option value="draft">Draft (pending review)</option>
            </select>
          </div>
          <div>
            <label class="field-label">Recipient name (test)</label>
            <input type="text" class="field" v-model="simName" />
          </div>
        </div>
      </div>
    </div>

    <!-- Order lookup -->
    <div class="card mb-5">
      <div class="card-header">
        <div class="card-title">🔍 Look up a specific order</div>
        <span class="text-[12px] text-gray-500 dark:text-dark-text-muted"
          >Optional — pre-fills scenario from a real order</span
        >
      </div>
      <div class="card-body">
        <div class="flex gap-2.5 items-end flex-wrap">
          <div class="flex-1 max-w-[280px]">
            <label class="field-label">Shopify order number</label>
            <input
              type="text"
              class="field"
              v-model="orderInput"
              placeholder="#100482"
              @input="orderError = false"
              @keydown.enter="lookupOrder"
            />
          </div>
          <button class="btn btn-outline" @click="lookupOrder">
            Look up order
          </button>
          <span class="text-[11px] text-gray-400 dark:text-dark-text-muted"
            >Try #100482 · #100391 · #100455</span
          >
        </div>

        <!-- Result -->
        <div
          v-show="orderResult"
          class="mt-3.5 px-4 py-3.5 rounded-lg border border-brand-teal border-opacity-20 bg-brand-light-blue bg-opacity-50 dark:bg-brand-teal dark:bg-opacity-10"
        >
          <div class="flex items-center justify-between mb-2.5">
            <div
              class="text-[13px] font-semibold text-brand-teal-dark dark:text-brand-light-blue"
            >
              {{ orderResult && orderResult.title }}
            </div>
            <button
              class="btn btn-ghost btn-xs text-brand-teal-dark dark:text-brand-light-blue font-semibold"
              @click="applyOrderToScenario"
            >
              <span>{{
                orderApplied ? "Applied ✓" : "Apply to scenario →"
              }}</span>
            </button>
          </div>
          <div class="grid grid-cols-2 sm:grid-cols-3 gap-3">
            <div v-for="f in orderFields" :key="f.label">
              <div
                class="text-[11px] text-gray-500 dark:text-dark-text-muted font-semibold uppercase tracking-wide"
              >
                {{ f.label }}
              </div>
              <div class="text-[13px] font-medium mt-0.5">{{ f.value }}</div>
            </div>
          </div>
          <div
            class="text-[12px] text-gray-500 dark:text-dark-text-muted mt-2.5"
          >
            ⚠️ In production this would pull live data from Shopify. This is a
            mock lookup for UI preview.
          </div>
        </div>

        <!-- Error -->
        <div
          v-show="orderError"
          class="mt-3 px-3 py-2.5 rounded-lg bg-brand-light-pink bg-opacity-50 dark:bg-brand-pink dark:bg-opacity-15 border border-brand-pink border-opacity-30 text-[13px] text-brand-pink-dark dark:text-brand-light-pink"
        >
          Order not found. Check the order number and try again.
        </div>
      </div>
    </div>

    <!-- Results -->
    <div class="card" ref="simResultsEl">
      <div class="card-header">
        <div class="card-title">▶ Simulation results</div>
        <div class="flex gap-2" v-show="simResults">
          <span class="badge badge-teal">{{
            simResults && simResults.summary
          }}</span>
          <span class="badge badge-live">{{
            simResults && simResults.countLabel
          }}</span>
        </div>
      </div>
      <div class="card-body space-y-4" :class="simRunning ? 'opacity-50' : ''">
        <div
          v-for="row in (simResults && simResults.rows) || []"
          :key="row.id"
          class="flex gap-4"
          :class="row.active ? '' : 'sms-bubble--skipped'"
        >
          <!-- time -->
          <div
            v-show="row.active"
            class="w-20 flex-shrink-0 text-right text-[12px] font-medium"
          >
            <span>{{ row.time && row.time.main }}</span
            ><br />
            <span class="text-[10px] text-gray-400 dark:text-dark-text-muted">{{
              row.time && row.time.sub
            }}</span>
          </div>
          <div
            v-show="!row.active"
            class="w-20 flex-shrink-0 text-right text-[12px] text-gray-400 dark:text-dark-text-muted"
          >
            — skipped —
          </div>
          <!-- body -->
          <div class="min-w-0">
            <div
              class="sms-bubble"
              :class="
                row.senderTone
                  ? 'sms-bubble--sender'
                  : row.active
                  ? ''
                  : 'sms-bubble--skipped'
              "
            >
              {{ row.bubble }}
            </div>
            <div
              v-show="row.active"
              class="flex flex-wrap gap-x-2 gap-y-1 mt-1.5 text-[11px] text-gray-500 dark:text-dark-text-muted"
            >
              <span v-for="(m, i) in row.meta || []" :key="i">
                <span v-show="i > 0" class="mr-2">·</span><span>{{ m }}</span>
              </span>
            </div>
            <div
              v-show="!row.active"
              class="mt-1.5 text-[11px] text-gray-400 dark:text-dark-text-muted"
            >
              {{ row.metaNote }}
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup>
import { onMounted } from "vue";
import { PlayIcon } from "vue-feather-icons";
import { useSmsSimulator } from "./composables/useSmsSimulator";

const {
  simType,
  simBuilding,
  simWindow,
  simWeather,
  simConsent,
  simTiming,
  simVersion,
  simName,
  simRunning,
  simResults,
  simResultsEl,
  orderInput,
  orderResult,
  orderError,
  orderApplied,
  orderFields,
  initSimulator,
  runSimulation,
  lookupOrder,
  applyOrderToScenario
} = useSmsSimulator();

onMounted(initSimulator);
</script>
